import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, Building2, Plus, Pencil, Trash2, Search, ChevronDown, ChevronUp, Wifi, ChevronLeft, ChevronRight, Calendar, Wallet } from 'lucide-react';

const AccountingHesapEkle = () => {
  const [activeTab, setActiveTab] = useState('kredi-karti');
  
  // Kredi Kartları state
  const [krediKartlari, setKrediKartlari] = useState([]);
  const [krediKartiModal, setKrediKartiModal] = useState(false);
  const [editingKrediKarti, setEditingKrediKarti] = useState(null);
  const [krediKartiForm, setKrediKartiForm] = useState({
    kart_basligi: '',
    fortis_subesi: '',
    banka_ismi: '',
    hesap_kesim_tarihi: '',
    son_odeme_tarihi: '',
    kart_no: ''
  });
  const [krediKartiSearch, setKrediKartiSearch] = useState('');
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedCardData, setExpandedCardData] = useState(null);
  const [cardTransactions, setCardTransactions] = useState([]);
  const [allCardTransactions, setAllCardTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  
  // Banka Hesapları state
  const [bankaHesaplari, setBankaHesaplari] = useState([]);
  const [bankaHesabiModal, setBankaHesabiModal] = useState(false);
  const [editingBankaHesabi, setEditingBankaHesabi] = useState(null);
  const [bankaHesabiForm, setBankaHesabiForm] = useState({
    banka_adi: '',
    banka_subesi: '',
    fortis_subesi: '',
    hesap_no: '',
    iban: ''
  });
  const [bankaHesabiSearch, setBankaHesabiSearch] = useState('');
  const [expandedBanka, setExpandedBanka] = useState(null);
  const [expandedBankaData, setExpandedBankaData] = useState(null);
  const [bankaTransactions, setBankaTransactions] = useState([]);
  const [loadingBankaTransactions, setLoadingBankaTransactions] = useState(false);
  const [bankaPeriod, setBankaPeriod] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  
  // Nakit Kasalar state
  const [nakitKasalar, setNakitKasalar] = useState([]);
  const [nakitKasaModal, setNakitKasaModal] = useState(false);
  const [editingNakitKasa, setEditingNakitKasa] = useState(null);
  const [nakitKasaForm, setNakitKasaForm] = useState({
    kasa_adi: '',
    sorumlu_kisi: '',
    fortis_subesi: ''
  });
  const [nakitKasaSearch, setNakitKasaSearch] = useState('');
  const [expandedKasa, setExpandedKasa] = useState(null);
  const [expandedKasaData, setExpandedKasaData] = useState(null);
  const [kasaTransactions, setKasaTransactions] = useState([]);
  const [loadingKasaTransactions, setLoadingKasaTransactions] = useState(false);
  const [kasaPeriod, setKasaPeriod] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  
  // Şubeler
  const [branches, setBranches] = useState([]);

  // Gün seçenekleri (1-31)
  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  const fetchBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Şubeler alınamadı:', error);
    }
  }, []);

  const fetchKrediKartlari = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/kredi-kartlari`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setKrediKartlari(data);
      }
    } catch (error) {
      console.error('Kredi kartları alınamadı:', error);
    }
  }, []);

  const fetchBankaHesaplari = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/banka-hesaplari`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBankaHesaplari(data);
      }
    } catch (error) {
      console.error('Banka hesapları alınamadı:', error);
    }
  }, []);

  const fetchNakitKasalar = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/nakit-kasalar`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNakitKasalar(data);
      }
    } catch (error) {
      console.error('Nakit kasalar alınamadı:', error);
    }
  }, []);

  const fetchCardTransactions = useCallback(async (kartBasligi) => {
    setLoadingTransactions(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Sadece çıkışları çek (kredi kartı harcamaları)
      const cikisRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`, { headers });
      
      let transactions = [];
      const normalizedKartBasligi = kartBasligi?.toLowerCase().trim();
      
      if (cikisRes.ok) {
        const cikislar = await cikisRes.json();
        const filteredCikislar = cikislar.filter(c => {
          if (c.hesap_tipi !== 'kredi_karti') return false;
          const hesapAdi = c.hesap_adi?.toLowerCase().trim() || '';
          return hesapAdi.includes(normalizedKartBasligi) || normalizedKartBasligi.includes(hesapAdi.split('(')[0].trim());
        }).map(c => ({ ...c, type: 'cikis' }));
        transactions = [...filteredCikislar];
      }
      
      transactions.sort((a, b) => new Date(b.tarih) - new Date(a.tarih));
      setAllCardTransactions(transactions);
    } catch (error) {
      console.error('İşlemler alınamadı:', error);
      toast.error('İşlemler yüklenirken hata oluştu');
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  // Banka hesabı işlemlerini çek
  const fetchBankaTransactions = useCallback(async (bankaAdi, iban) => {
    setLoadingBankaTransactions(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [girisRes, cikisRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/girisler`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`, { headers })
      ]);
      
      let transactions = [];
      const normalizedBankaAdi = bankaAdi?.toLowerCase().trim();
      
      if (girisRes.ok) {
        const girisler = await girisRes.json();
        const filteredGirisler = girisler.filter(g => {
          if (g.hesap_tipi !== 'banka_hesabi') return false;
          const hesapAdi = g.hesap_adi?.toLowerCase().trim() || '';
          return hesapAdi.includes(normalizedBankaAdi) || normalizedBankaAdi.includes(hesapAdi.split('(')[0].trim());
        }).map(g => ({ ...g, type: 'giris' }));
        transactions = [...transactions, ...filteredGirisler];
      }
      
      if (cikisRes.ok) {
        const cikislar = await cikisRes.json();
        const filteredCikislar = cikislar.filter(c => {
          if (c.hesap_tipi !== 'banka_hesabi') return false;
          const hesapAdi = c.hesap_adi?.toLowerCase().trim() || '';
          return hesapAdi.includes(normalizedBankaAdi) || normalizedBankaAdi.includes(hesapAdi.split('(')[0].trim());
        }).map(c => ({ ...c, type: 'cikis' }));
        transactions = [...transactions, ...filteredCikislar];
      }
      
      transactions.sort((a, b) => new Date(b.tarih) - new Date(a.tarih));
      setBankaTransactions(transactions);
    } catch (error) {
      console.error('Banka işlemleri alınamadı:', error);
      toast.error('İşlemler yüklenirken hata oluştu');
    } finally {
      setLoadingBankaTransactions(false);
    }
  }, []);

  // Nakit kasa işlemlerini çek
  const fetchKasaTransactions = useCallback(async (kasaAdi, kasaId) => {
    setLoadingKasaTransactions(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [girisRes, cikisRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/girisler`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`, { headers })
      ]);
      
      let transactions = [];
      const normalizedKasaAdi = kasaAdi?.toLowerCase().trim();
      
      if (girisRes.ok) {
        const girisler = await girisRes.json();
        girisler.forEach(g => {
          // Kasadan para alındı (cari_hesap = kasa) -> Kasadan ÇIKIŞ
          const isCariKasa = g.kasa_id === kasaId || g.cari_hesap_id === kasaId || 
            (g.cari_hesap_adi?.toLowerCase().includes(normalizedKasaAdi));
          
          // Kasaya para girdi (hesap = kasa, hesap_tipi = nakit_kasa) -> Kasaya GİRİŞ
          const isHesapKasa = (g.hesap_id === kasaId && g.hesap_tipi === 'nakit_kasa') ||
            (g.hesap_tipi === 'nakit_kasa' && g.hesap_adi?.toLowerCase().includes(normalizedKasaAdi));
          
          if (isCariKasa) {
            transactions.push({ ...g, type: 'cikis' }); // Kasadan çıkış
          }
          if (isHesapKasa) {
            transactions.push({ ...g, type: 'giris' }); // Kasaya giriş
          }
        });
      }
      
      if (cikisRes.ok) {
        const cikislar = await cikisRes.json();
        cikislar.forEach(c => {
          // Kasaya ödeme yapıldı (cari_hesap = kasa) -> Kasaya GİRİŞ
          const isCariKasa = c.kasa_id === kasaId || c.cari_hesap_id === kasaId ||
            (c.cari_hesap_adi?.toLowerCase().includes(normalizedKasaAdi));
          
          // Kasadan ödeme yapıldı (hesap = kasa, hesap_tipi = nakit_kasa) -> Kasadan ÇIKIŞ
          const isHesapKasa = (c.hesap_id === kasaId && c.hesap_tipi === 'nakit_kasa') ||
            (c.hesap_tipi === 'nakit_kasa' && c.hesap_adi?.toLowerCase().includes(normalizedKasaAdi));
          
          if (isCariKasa) {
            transactions.push({ ...c, type: 'giris' }); // Kasaya giriş
          }
          if (isHesapKasa) {
            transactions.push({ ...c, type: 'cikis' }); // Kasadan çıkış
          }
        });
      }
      
      // Tarihe göre sırala
      transactions.sort((a, b) => new Date(b.tarih) - new Date(a.tarih));
      setKasaTransactions(transactions);
    } catch (error) {
      console.error('Kasa işlemleri alınamadı:', error);
      toast.error('İşlemler yüklenirken hata oluştu');
    } finally {
      setLoadingKasaTransactions(false);
    }
  }, []);

  // Hesap kesim dönemine göre işlemleri filtrele
  const filteredTransactions = useMemo(() => {
    if (!expandedCardData || !allCardTransactions.length) return [];
    
    const hesapKesimGunu = parseInt(expandedCardData.hesap_kesim_tarihi) || 1;
    const { month, year } = selectedPeriod;
    
    // Dönem başlangıcı: Önceki ayın hesap kesim günü + 1
    let periodStart = new Date(year, month - 1, hesapKesimGunu + 1);
    // Dönem bitişi: Bu ayın hesap kesim günü
    let periodEnd = new Date(year, month, hesapKesimGunu, 23, 59, 59);
    
    // Eğer başlangıç sonu geçerse düzelt
    if (periodStart > periodEnd) {
      periodStart = new Date(year, month, hesapKesimGunu + 1);
      periodEnd = new Date(year, month + 1, hesapKesimGunu, 23, 59, 59);
    }
    
    return allCardTransactions.filter(tx => {
      const txDate = new Date(tx.tarih);
      return txDate >= periodStart && txDate <= periodEnd;
    });
  }, [allCardTransactions, expandedCardData, selectedPeriod]);

  // Banka hesabı dönem filtreleme
  const filteredBankaTransactions = useMemo(() => {
    if (!bankaTransactions.length) return [];
    
    const { month, year } = bankaPeriod;
    const periodStart = new Date(year, month, 1);
    const periodEnd = new Date(year, month + 1, 0, 23, 59, 59);
    
    return bankaTransactions.filter(tx => {
      const txDate = new Date(tx.tarih);
      return txDate >= periodStart && txDate <= periodEnd;
    });
  }, [bankaTransactions, bankaPeriod]);

  // Nakit kasa dönem filtreleme
  const filteredKasaTransactions = useMemo(() => {
    if (!kasaTransactions.length) return [];
    
    const { month, year } = kasaPeriod;
    const periodStart = new Date(year, month, 1);
    const periodEnd = new Date(year, month + 1, 0, 23, 59, 59);
    
    return kasaTransactions.filter(tx => {
      const txDate = new Date(tx.tarih);
      return txDate >= periodStart && txDate <= periodEnd;
    });
  }, [kasaTransactions, kasaPeriod]);

  // Banka dönem toplamları
  const bankaPeriodTotals = useMemo(() => {
    if (!filteredBankaTransactions || filteredBankaTransactions.length === 0) {
      return { giris: 0, cikis: 0, bakiye: 0 };
    }
    const giris = filteredBankaTransactions.filter(t => t.type === 'giris').reduce((sum, t) => sum + (parseFloat(t.miktar) || 0), 0);
    const cikis = filteredBankaTransactions.filter(t => t.type === 'cikis').reduce((sum, t) => sum + (parseFloat(t.miktar) || 0), 0);
    return { giris: isNaN(giris) ? 0 : giris, cikis: isNaN(cikis) ? 0 : cikis, bakiye: giris - cikis };
  }, [filteredBankaTransactions]);

  // Nakit kasa dönem toplamları
  const kasaPeriodTotals = useMemo(() => {
    if (!filteredKasaTransactions || filteredKasaTransactions.length === 0) {
      return { giris: 0, cikis: 0, bakiye: 0 };
    }
    const giris = filteredKasaTransactions.filter(t => t.type === 'giris').reduce((sum, t) => sum + (parseFloat(t.miktar) || 0), 0);
    const cikis = filteredKasaTransactions.filter(t => t.type === 'cikis').reduce((sum, t) => sum + (parseFloat(t.miktar) || 0), 0);
    return { giris: isNaN(giris) ? 0 : giris, cikis: isNaN(cikis) ? 0 : cikis, bakiye: giris - cikis };
  }, [filteredKasaTransactions]);

  useEffect(() => {
    fetchBranches();
    fetchKrediKartlari();
    fetchBankaHesaplari();
    fetchNakitKasalar();
  }, [fetchBranches, fetchKrediKartlari, fetchBankaHesaplari, fetchNakitKasalar]);

  const toggleCardExpansion = (kart) => {
    if (expandedCard === kart.id) {
      setExpandedCard(null);
      setExpandedCardData(null);
      setAllCardTransactions([]);
    } else {
      setExpandedCard(kart.id);
      setExpandedCardData(kart);
      setSelectedPeriod({ month: new Date().getMonth(), year: new Date().getFullYear() });
      fetchCardTransactions(kart.kart_basligi);
    }
  };

  const toggleBankaExpansion = (hesap) => {
    if (expandedBanka === hesap.id) {
      setExpandedBanka(null);
      setExpandedBankaData(null);
      setBankaTransactions([]);
    } else {
      setExpandedBanka(hesap.id);
      setExpandedBankaData(hesap);
      setBankaPeriod({ month: new Date().getMonth(), year: new Date().getFullYear() });
      fetchBankaTransactions(hesap.banka_adi, hesap.iban);
    }
  };

  const toggleKasaExpansion = (kasa) => {
    if (expandedKasa === kasa.id) {
      setExpandedKasa(null);
      setExpandedKasaData(null);
      setKasaTransactions([]);
    } else {
      setExpandedKasa(kasa.id);
      setExpandedKasaData(kasa);
      setKasaPeriod({ month: new Date().getMonth(), year: new Date().getFullYear() });
      fetchKasaTransactions(kasa.kasa_adi, kasa.id);
    }
  };

  const navigateBankaPeriod = (direction) => {
    setBankaPeriod(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;
      
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
      
      return { month: newMonth, year: newYear };
    });
  };

  const navigateKasaPeriod = (direction) => {
    setKasaPeriod(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;
      
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
      
      return { month: newMonth, year: newYear };
    });
  };

  const navigatePeriod = (direction) => {
    setSelectedPeriod(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;
      
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
      
      return { month: newMonth, year: newYear };
    });
  };

  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  const getPeriodLabel = () => {
    if (!expandedCardData) return '';
    const hesapKesimGunu = parseInt(expandedCardData.hesap_kesim_tarihi) || 1;
    const { month, year } = selectedPeriod;
    
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    
    return `${hesapKesimGunu + 1} ${monthNames[prevMonth]} ${prevYear} - ${hesapKesimGunu} ${monthNames[month]} ${year}`;
  };

  // Kredi Kartı İşlemleri
  const handleKrediKartiSubmit = async () => {
    if (!krediKartiForm.kart_basligi || !krediKartiForm.banka_ismi) {
      toast.error('Kart başlığı ve banka ismi gereklidir');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const url = editingKrediKarti 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/kredi-kartlari/${editingKrediKarti.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/kredi-kartlari`;
      
      const response = await fetch(url, {
        method: editingKrediKarti ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(krediKartiForm)
      });

      if (response.ok) {
        toast.success(editingKrediKarti ? 'Kredi kartı güncellendi' : 'Kredi kartı eklendi');
        setKrediKartiModal(false);
        setEditingKrediKarti(null);
        setKrediKartiForm({
          kart_basligi: '',
          fortis_subesi: '',
          banka_ismi: '',
          hesap_kesim_tarihi: '',
          son_odeme_tarihi: '',
          kart_no: ''
        });
        fetchKrediKartlari();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDeleteKrediKarti = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu kredi kartını silmek istediğinizden emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/kredi-kartlari/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Kredi kartı silindi');
        fetchKrediKartlari();
        if (expandedCard === id) {
          setExpandedCard(null);
          setExpandedCardData(null);
          setAllCardTransactions([]);
        }
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const openEditKrediKarti = (kart, e) => {
    e.stopPropagation();
    setEditingKrediKarti(kart);
    setKrediKartiForm({
      kart_basligi: kart.kart_basligi || '',
      fortis_subesi: kart.fortis_subesi || '',
      banka_ismi: kart.banka_ismi || '',
      hesap_kesim_tarihi: kart.hesap_kesim_tarihi || '',
      son_odeme_tarihi: kart.son_odeme_tarihi || '',
      kart_no: kart.kart_no || ''
    });
    setKrediKartiModal(true);
  };

  // Banka Hesabı İşlemleri
  const handleBankaHesabiSubmit = async () => {
    if (!bankaHesabiForm.banka_adi || !bankaHesabiForm.iban) {
      toast.error('Banka adı ve IBAN gereklidir');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const url = editingBankaHesabi 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/banka-hesaplari/${editingBankaHesabi.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/banka-hesaplari`;
      
      const response = await fetch(url, {
        method: editingBankaHesabi ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bankaHesabiForm)
      });

      if (response.ok) {
        toast.success(editingBankaHesabi ? 'Banka hesabı güncellendi' : 'Banka hesabı eklendi');
        setBankaHesabiModal(false);
        setEditingBankaHesabi(null);
        setBankaHesabiForm({
          banka_adi: '',
          banka_subesi: '',
          fortis_subesi: '',
          hesap_no: '',
          iban: ''
        });
        fetchBankaHesaplari();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDeleteBankaHesabi = async (id) => {
    if (!window.confirm('Bu banka hesabını silmek istediğinizden emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/banka-hesaplari/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Banka hesabı silindi');
        fetchBankaHesaplari();
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const openEditBankaHesabi = (hesap) => {
    setEditingBankaHesabi(hesap);
    setBankaHesabiForm({
      banka_adi: hesap.banka_adi || '',
      banka_subesi: hesap.banka_subesi || '',
      fortis_subesi: hesap.fortis_subesi || '',
      hesap_no: hesap.hesap_no || '',
      iban: hesap.iban || ''
    });
    setBankaHesabiModal(true);
  };

  // Nakit Kasa İşlemleri
  const handleNakitKasaSubmit = async () => {
    if (!nakitKasaForm.kasa_adi || !nakitKasaForm.sorumlu_kisi) {
      toast.error('Kasa adı ve sorumlu kişi gereklidir');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const url = editingNakitKasa 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/nakit-kasalar/${editingNakitKasa.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/nakit-kasalar`;
      
      const response = await fetch(url, {
        method: editingNakitKasa ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(nakitKasaForm)
      });

      if (response.ok) {
        toast.success(editingNakitKasa ? 'Nakit kasa güncellendi' : 'Nakit kasa eklendi');
        setNakitKasaModal(false);
        setEditingNakitKasa(null);
        setNakitKasaForm({
          kasa_adi: '',
          sorumlu_kisi: '',
          fortis_subesi: ''
        });
        fetchNakitKasalar();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDeleteNakitKasa = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu nakit kasayı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/nakit-kasalar/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Nakit kasa silindi');
        fetchNakitKasalar();
        if (expandedKasa === id) {
          setExpandedKasa(null);
          setExpandedKasaData(null);
          setKasaTransactions([]);
        }
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const openEditNakitKasa = (kasa, e) => {
    e.stopPropagation();
    setEditingNakitKasa(kasa);
    setNakitKasaForm({
      kasa_adi: kasa.kasa_adi || '',
      sorumlu_kisi: kasa.sorumlu_kisi || '',
      fortis_subesi: kasa.fortis_subesi || ''
    });
    setNakitKasaModal(true);
  };

  // Filtreleme
  const filteredKrediKartlari = krediKartlari.filter(kart => 
    kart.kart_basligi?.toLowerCase().includes(krediKartiSearch.toLowerCase()) ||
    kart.banka_ismi?.toLowerCase().includes(krediKartiSearch.toLowerCase()) ||
    kart.fortis_subesi?.toLowerCase().includes(krediKartiSearch.toLowerCase())
  );

  const filteredBankaHesaplari = bankaHesaplari.filter(hesap => 
    hesap.banka_adi?.toLowerCase().includes(bankaHesabiSearch.toLowerCase()) ||
    hesap.iban?.toLowerCase().includes(bankaHesabiSearch.toLowerCase()) ||
    hesap.fortis_subesi?.toLowerCase().includes(bankaHesabiSearch.toLowerCase())
  );

  const filteredNakitKasalar = nakitKasalar.filter(kasa => 
    kasa.kasa_adi?.toLowerCase().includes(nakitKasaSearch.toLowerCase()) ||
    kasa.sorumlu_kisi?.toLowerCase().includes(nakitKasaSearch.toLowerCase()) ||
    kasa.fortis_subesi?.toLowerCase().includes(nakitKasaSearch.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (value, currency = 'TL') => {
    if (value === null || value === undefined || isNaN(value)) return '₺0,00';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: currency === 'TL' ? 'TRY' : currency 
    }).format(value);
  };

  // Kart renkleri
  const cardColors = [
    'from-slate-800 via-slate-700 to-slate-900',
    'from-indigo-800 via-indigo-700 to-indigo-900',
    'from-emerald-800 via-emerald-700 to-emerald-900',
    'from-rose-800 via-rose-700 to-rose-900',
    'from-amber-700 via-amber-600 to-amber-800',
    'from-violet-800 via-violet-700 to-violet-900',
    'from-cyan-800 via-cyan-700 to-cyan-900',
  ];

  const getCardColor = (index) => cardColors[index % cardColors.length];

  const formatCardNumber = (cardNo) => {
    if (!cardNo) return '**** **** **** ****';
    const cleaned = cardNo.replace(/\s/g, '');
    const masked = cleaned.slice(0, -4).replace(/./g, '*') + cleaned.slice(-4);
    return masked.replace(/(.{4})/g, '$1 ').trim();
  };

  // Dönem toplamları - sadece çıkışlar
  const periodTotals = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return { cikis: 0 };
    }
    const cikis = filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.miktar) || 0), 0);
    return { cikis: isNaN(cikis) ? 0 : cikis };
  }, [filteredTransactions]);

  return (
    <div className="space-y-6" data-testid="hesap-ekle-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hesaplar</h1>
          <p className="text-gray-600 mt-1">Kredi kartları, banka hesapları ve nakit kasaları yönetin</p>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500 rounded-lg">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Kredi Kartları</p>
                <p className="text-2xl font-bold text-purple-900">{krediKartlari.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500 rounded-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Banka Hesapları</p>
                <p className="text-2xl font-bold text-blue-900">{bankaHesaplari.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500 rounded-lg">
                <Wallet className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">Nakit Kasalar</p>
                <p className="text-2xl font-bold text-emerald-900">{nakitKasalar.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="kredi-karti" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Kredi Kartı
          </TabsTrigger>
          <TabsTrigger value="banka-hesabi" className="gap-2">
            <Building2 className="h-4 w-4" />
            Banka Hesabı
          </TabsTrigger>
          <TabsTrigger value="nakit-kasa" className="gap-2">
            <Wallet className="h-4 w-4" />
            Nakit Kasa
          </TabsTrigger>
        </TabsList>

        {/* Kredi Kartı Tab */}
        <TabsContent value="kredi-karti" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                  Kredi Kartları
                </CardTitle>
                <Button 
                  onClick={() => {
                    setEditingKrediKarti(null);
                    setKrediKartiForm({
                      kart_basligi: '',
                      fortis_subesi: '',
                      banka_ismi: '',
                      hesap_kesim_tarihi: '',
                      son_odeme_tarihi: '',
                      kart_no: ''
                    });
                    setKrediKartiModal(true);
                  }}
                  className="gap-2 bg-purple-600 hover:bg-purple-700"
                  data-testid="add-kredi-karti-btn"
                >
                  <Plus className="h-4 w-4" />
                  Kredi Kartı Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Arama */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Kart adı, banka veya şube ara..."
                  value={krediKartiSearch}
                  onChange={(e) => setKrediKartiSearch(e.target.value)}
                  className="pl-10"
                  data-testid="kredi-karti-search"
                />
              </div>

              {/* Kredi Kartları Grid */}
              {filteredKrediKartlari.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Kredi kartı bulunamadı</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredKrediKartlari.map((kart, index) => (
                    <div key={kart.id} className="space-y-0">
                      {/* Kredi Kartı Görünümü */}
                      <div 
                        className={`relative w-full max-w-md mx-auto cursor-pointer transition-all duration-300 ${expandedCard === kart.id ? 'scale-105' : 'hover:scale-102'}`}
                        onClick={() => toggleCardExpansion(kart)}
                        data-testid={`kredi-karti-card-${kart.id}`}
                      >
                        <div className={`bg-gradient-to-br ${getCardColor(index)} rounded-2xl p-6 text-white shadow-xl aspect-[1.6/1] relative overflow-hidden`}>
                          {/* Dekoratif Elementler */}
                          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
                          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
                          
                          {/* Üst Kısım - Banka & Wifi */}
                          <div className="flex justify-between items-start relative z-10">
                            <div>
                              <p className="text-white/70 text-xs uppercase tracking-wider">Banka</p>
                              <p className="font-bold text-lg">{kart.banka_ismi}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Wifi className="h-6 w-6 text-white/80 rotate-90" />
                            </div>
                          </div>

                          {/* Chip */}
                          <div className="mt-4 relative z-10">
                            <div className="w-12 h-9 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 rounded-md flex items-center justify-center">
                              <div className="w-8 h-6 border border-yellow-600/30 rounded-sm grid grid-cols-3 gap-px p-1">
                                {[...Array(6)].map((_, i) => (
                                  <div key={i} className="bg-yellow-600/20 rounded-sm"></div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Kart Numarası */}
                          <div className="mt-4 relative z-10">
                            <p className="font-mono text-xl tracking-[0.2em] text-white/90">
                              {formatCardNumber(kart.kart_no)}
                            </p>
                          </div>

                          {/* Alt Kısım */}
                          <div className="mt-4 flex justify-between items-end relative z-10">
                            <div>
                              <p className="text-white/60 text-[10px] uppercase">Kart Adı</p>
                              <p className="font-semibold text-sm uppercase tracking-wide">{kart.kart_basligi}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white/60 text-[10px] uppercase">Hesap Kesim</p>
                              <p className="font-semibold">{kart.hesap_kesim_tarihi || '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white/60 text-[10px] uppercase">Son Ödeme</p>
                              <p className="font-semibold">{kart.son_odeme_tarihi || '-'}</p>
                            </div>
                          </div>

                          {/* Şube Rozeti */}
                          {kart.fortis_subesi && (
                            <div className="absolute top-6 right-6 z-10">
                              <Badge className="bg-white/20 text-white border-0 text-xs">
                                {kart.fortis_subesi}
                              </Badge>
                            </div>
                          )}

                          {/* Aksiyon Butonları */}
                          <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 bg-white/10 hover:bg-white/20 text-white"
                              onClick={(e) => openEditKrediKarti(kart, e)}
                              data-testid={`edit-kredi-karti-${kart.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 bg-white/10 hover:bg-red-500/50 text-white"
                              onClick={(e) => handleDeleteKrediKarti(kart.id, e)}
                              data-testid={`delete-kredi-karti-${kart.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Genişletme İkonu */}
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                            {expandedCard === kart.id ? (
                              <ChevronUp className="h-5 w-5 text-white/60 animate-bounce" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-white/60 animate-bounce" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* İşlemler Tablosu */}
                      {expandedCard === kart.id && (
                        <div className="max-w-4xl mx-auto mt-4 bg-white rounded-xl border shadow-lg overflow-hidden animate-in slide-in-from-top-2 duration-300">
                          {/* Dönem Navigasyonu */}
                          <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-white hover:bg-white/20"
                              onClick={() => navigatePeriod(-1)}
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <div className="text-center text-white">
                              <p className="text-sm opacity-80">Hesap Kesim Dönemi</p>
                              <p className="font-semibold flex items-center gap-2 justify-center">
                                <Calendar className="h-4 w-4" />
                                {getPeriodLabel()}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-white hover:bg-white/20"
                              onClick={() => navigatePeriod(1)}
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Button>
                          </div>

                          {/* Dönem Özeti - Sadece Toplam Çıkış */}
                          <div className="p-4 bg-gray-50 border-b">
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Dönem Toplam Harcama</p>
                              <p className="text-2xl font-bold text-red-600">{formatCurrency(periodTotals.cikis)}</p>
                            </div>
                          </div>
                          
                          {loadingTransactions ? (
                            <div className="p-8 text-center text-gray-500">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                              İşlemler yükleniyor...
                            </div>
                          ) : filteredTransactions.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <CreditCard className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                              Bu dönemde işlem bulunamadı
                            </div>
                          ) : (
                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                              <Table>
                                <TableHeader className="sticky top-0 bg-white">
                                  <TableRow className="bg-gray-50">
                                    <TableHead>Tarih</TableHead>
                                    <TableHead>Etiket</TableHead>
                                    <TableHead>Açıklama</TableHead>
                                    <TableHead>Cari Hesap</TableHead>
                                    <TableHead className="text-right">Tutar</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filteredTransactions.map((tx) => (
                                    <TableRow key={tx.id} className="hover:bg-gray-50">
                                      <TableCell className="font-medium whitespace-nowrap">
                                        {formatDate(tx.tarih)}
                                      </TableCell>
                                      <TableCell>
                                        {tx.etiket_adi ? (
                                          <Badge variant="outline" className="text-xs">
                                            {tx.etiket_adi}
                                          </Badge>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="max-w-[250px]">
                                        {tx.aciklama || '-'}
                                      </TableCell>
                                      <TableCell>
                                        {tx.cari_hesap_adi || '-'}
                                      </TableCell>
                                      <TableCell className="text-right font-semibold text-red-600 whitespace-nowrap">
                                        {formatCurrency(tx.miktar, tx.para_birimi)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                          
                          {/* Alt Bilgi */}
                          <div className="bg-gray-50 px-6 py-3 border-t flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              Bu dönemde toplam {filteredTransactions.length} işlem
                            </span>
                            <span className="text-sm font-semibold text-red-600">
                              Toplam: {formatCurrency(periodTotals.cikis)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banka Hesabı Tab */}
        <TabsContent value="banka-hesabi" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Banka Hesapları
                </CardTitle>
                <Button 
                  onClick={() => {
                    setEditingBankaHesabi(null);
                    setBankaHesabiForm({
                      banka_adi: '',
                      banka_subesi: '',
                      fortis_subesi: '',
                      hesap_no: '',
                      iban: ''
                    });
                    setBankaHesabiModal(true);
                  }}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="add-banka-hesabi-btn"
                >
                  <Plus className="h-4 w-4" />
                  Banka Hesabı Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Arama */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Banka adı, IBAN veya şube ara..."
                  value={bankaHesabiSearch}
                  onChange={(e) => setBankaHesabiSearch(e.target.value)}
                  className="pl-10"
                  data-testid="banka-hesabi-search"
                />
              </div>

              {/* Banka Hesapları Kartları */}
              {filteredBankaHesaplari.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Banka hesabı bulunamadı</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredBankaHesaplari.map((hesap, index) => (
                    <div key={hesap.id} className="space-y-0">
                      {/* Banka Cüzdanı Kartı */}
                      <div 
                        className={`relative w-full max-w-md mx-auto cursor-pointer transition-all duration-300 ${expandedBanka === hesap.id ? 'scale-105' : 'hover:scale-102'}`}
                        onClick={() => toggleBankaExpansion(hesap)}
                        data-testid={`banka-hesabi-card-${hesap.id}`}
                      >
                        <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-800 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
                          {/* Dekoratif Elementler */}
                          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-24 translate-x-24"></div>
                          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16"></div>
                          
                          {/* Üst Kısım - Banka Adı */}
                          <div className="flex justify-between items-start relative z-10">
                            <div>
                              <p className="text-white/70 text-xs uppercase tracking-wider">Banka</p>
                              <p className="font-bold text-xl">{hesap.banka_adi}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-8 w-8 text-white/60" />
                            </div>
                          </div>

                          {/* Banka Şubesi */}
                          {hesap.banka_subesi && (
                            <div className="mt-3 relative z-10">
                              <p className="text-white/60 text-xs">Şube</p>
                              <p className="text-sm">{hesap.banka_subesi}</p>
                            </div>
                          )}

                          {/* IBAN */}
                          <div className="mt-4 relative z-10">
                            <p className="text-white/60 text-xs">IBAN</p>
                            <p className="font-mono text-sm tracking-wide text-white/90">
                              {hesap.iban || 'TR** **** **** **** **** **** **'}
                            </p>
                          </div>

                          {/* Hesap No */}
                          {hesap.hesap_no && (
                            <div className="mt-3 relative z-10">
                              <p className="text-white/60 text-xs">Hesap No</p>
                              <p className="font-mono text-sm">{hesap.hesap_no}</p>
                            </div>
                          )}

                          {/* Şube Rozeti */}
                          {hesap.fortis_subesi && (
                            <div className="absolute top-6 right-6 z-10">
                              <Badge className="bg-white/20 text-white border-0 text-xs">
                                {hesap.fortis_subesi}
                              </Badge>
                            </div>
                          )}

                          {/* Aksiyon Butonları */}
                          <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 bg-white/10 hover:bg-white/20 text-white"
                              onClick={(e) => { e.stopPropagation(); openEditBankaHesabi(hesap); }}
                              data-testid={`edit-banka-hesabi-${hesap.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 bg-white/10 hover:bg-red-500/50 text-white"
                              onClick={(e) => { e.stopPropagation(); handleDeleteBankaHesabi(hesap.id); }}
                              data-testid={`delete-banka-hesabi-${hesap.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Genişletme İkonu */}
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                            {expandedBanka === hesap.id ? (
                              <ChevronUp className="h-5 w-5 text-white/60 animate-bounce" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-white/60 animate-bounce" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* İşlemler Tablosu */}
                      {expandedBanka === hesap.id && (
                        <div className="max-w-4xl mx-auto mt-4 bg-white rounded-xl border shadow-lg overflow-hidden animate-in slide-in-from-top-2 duration-300">
                          {/* Dönem Navigasyonu */}
                          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-white hover:bg-white/20"
                              onClick={() => navigateBankaPeriod(-1)}
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <div className="text-center text-white">
                              <p className="text-sm opacity-80">Hesap Hareketi</p>
                              <p className="font-semibold flex items-center gap-2 justify-center">
                                <Calendar className="h-4 w-4" />
                                {monthNames[bankaPeriod.month]} {bankaPeriod.year}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-white hover:bg-white/20"
                              onClick={() => navigateBankaPeriod(1)}
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Button>
                          </div>

                          {/* Dönem Özeti */}
                          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b">
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Giriş</p>
                              <p className="text-lg font-bold text-green-600">{formatCurrency(bankaPeriodTotals.giris)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Çıkış</p>
                              <p className="text-lg font-bold text-red-600">{formatCurrency(bankaPeriodTotals.cikis)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Bakiye</p>
                              <p className={`text-lg font-bold ${bankaPeriodTotals.bakiye >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(bankaPeriodTotals.bakiye)}
                              </p>
                            </div>
                          </div>
                          
                          {loadingBankaTransactions ? (
                            <div className="p-8 text-center text-gray-500">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                              İşlemler yükleniyor...
                            </div>
                          ) : filteredBankaTransactions.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <Building2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                              Bu dönemde işlem bulunamadı
                            </div>
                          ) : (
                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                              <Table>
                                <TableHeader className="sticky top-0 bg-white">
                                  <TableRow className="bg-gray-50">
                                    <TableHead className="w-12">Tip</TableHead>
                                    <TableHead>Tarih</TableHead>
                                    <TableHead>Etiket</TableHead>
                                    <TableHead>Açıklama</TableHead>
                                    <TableHead>Cari Hesap</TableHead>
                                    <TableHead className="text-right">Tutar</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filteredBankaTransactions.map((tx) => (
                                    <TableRow key={tx.id} className="hover:bg-gray-50">
                                      <TableCell>
                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${tx.type === 'giris' ? 'bg-green-100' : 'bg-red-100'}`}>
                                          {tx.type === 'giris' ? (
                                            <ChevronDown className="h-4 w-4 text-green-600" />
                                          ) : (
                                            <ChevronUp className="h-4 w-4 text-red-600" />
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="font-medium whitespace-nowrap">
                                        {formatDate(tx.tarih)}
                                      </TableCell>
                                      <TableCell>
                                        {tx.etiket_adi ? (
                                          <Badge variant="outline" className="text-xs">
                                            {tx.etiket_adi}
                                          </Badge>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="max-w-[200px]">
                                        {tx.aciklama || '-'}
                                      </TableCell>
                                      <TableCell>
                                        {tx.cari_hesap_adi || '-'}
                                      </TableCell>
                                      <TableCell className={`text-right font-semibold whitespace-nowrap ${tx.type === 'giris' ? 'text-green-600' : 'text-red-600'}`}>
                                        {tx.type === 'giris' ? '+' : '-'} {formatCurrency(tx.miktar, tx.para_birimi)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                          
                          {/* Alt Bilgi */}
                          <div className="bg-gray-50 px-6 py-3 border-t flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              Bu dönemde toplam {filteredBankaTransactions.length} işlem
                            </span>
                            <div className="flex gap-4 text-sm">
                              <span className="text-green-600 font-medium">Giriş: {formatCurrency(bankaPeriodTotals.giris)}</span>
                              <span className="text-red-600 font-medium">Çıkış: {formatCurrency(bankaPeriodTotals.cikis)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nakit Kasa Tab */}
        <TabsContent value="nakit-kasa" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-emerald-600" />
                  Nakit Kasalar
                </CardTitle>
                <Button 
                  onClick={() => {
                    setEditingNakitKasa(null);
                    setNakitKasaForm({
                      kasa_adi: '',
                      sorumlu_kisi: '',
                      fortis_subesi: ''
                    });
                    setNakitKasaModal(true);
                  }}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-nakit-kasa-btn"
                >
                  <Plus className="h-4 w-4" />
                  Nakit Kasa Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Arama */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Kasa adı, sorumlu kişi veya şube ara..."
                  value={nakitKasaSearch}
                  onChange={(e) => setNakitKasaSearch(e.target.value)}
                  className="pl-10"
                  data-testid="nakit-kasa-search"
                />
              </div>

              {/* Nakit Kasa Kartları */}
              {filteredNakitKasalar.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Nakit kasa bulunamadı</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredNakitKasalar.map((kasa, index) => (
                    <div key={kasa.id} className="space-y-0">
                      {/* Nakit Kasa Cüzdan Kartı - Yeşil Tema */}
                      <div 
                        className={`relative w-full max-w-md mx-auto cursor-pointer transition-all duration-300 ${expandedKasa === kasa.id ? 'scale-105' : 'hover:scale-102'}`}
                        onClick={() => toggleKasaExpansion(kasa)}
                        data-testid={`nakit-kasa-card-${kasa.id}`}
                      >
                        <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-800 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
                          {/* Dekoratif Elementler */}
                          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-24 translate-x-24"></div>
                          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16"></div>
                          
                          {/* Üst Kısım - Kasa Adı */}
                          <div className="flex justify-between items-start relative z-10">
                            <div>
                              <p className="text-white/70 text-xs uppercase tracking-wider">Nakit Kasa</p>
                              <p className="font-bold text-xl">{kasa.kasa_adi}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Wallet className="h-8 w-8 text-white/60" />
                            </div>
                          </div>

                          {/* Sorumlu Kişi */}
                          <div className="mt-6 relative z-10">
                            <p className="text-white/60 text-xs uppercase">Sorumlu Kişi</p>
                            <p className="text-lg font-semibold">{kasa.sorumlu_kisi}</p>
                          </div>

                          {/* Şube Rozeti */}
                          {kasa.fortis_subesi && (
                            <div className="absolute top-6 right-6 z-10">
                              <Badge className="bg-white/20 text-white border-0 text-xs">
                                {kasa.fortis_subesi}
                              </Badge>
                            </div>
                          )}

                          {/* Aksiyon Butonları */}
                          <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 bg-white/10 hover:bg-white/20 text-white"
                              onClick={(e) => openEditNakitKasa(kasa, e)}
                              data-testid={`edit-nakit-kasa-${kasa.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 bg-white/10 hover:bg-red-500/50 text-white"
                              onClick={(e) => handleDeleteNakitKasa(kasa.id, e)}
                              data-testid={`delete-nakit-kasa-${kasa.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Genişletme İkonu */}
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                            {expandedKasa === kasa.id ? (
                              <ChevronUp className="h-5 w-5 text-white/60 animate-bounce" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-white/60 animate-bounce" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* İşlemler Tablosu */}
                      {expandedKasa === kasa.id && (
                        <div className="max-w-4xl mx-auto mt-4 bg-white rounded-xl border shadow-lg overflow-hidden animate-in slide-in-from-top-2 duration-300">
                          {/* Dönem Navigasyonu */}
                          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 flex items-center justify-between">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-white hover:bg-white/20"
                              onClick={() => navigateKasaPeriod(-1)}
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <div className="text-center text-white">
                              <p className="text-sm opacity-80">Kasa Hareketi</p>
                              <p className="font-semibold flex items-center gap-2 justify-center">
                                <Calendar className="h-4 w-4" />
                                {monthNames[kasaPeriod.month]} {kasaPeriod.year}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-white hover:bg-white/20"
                              onClick={() => navigateKasaPeriod(1)}
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Button>
                          </div>

                          {/* Dönem Özeti */}
                          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b">
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Giriş</p>
                              <p className="text-lg font-bold text-green-600">{formatCurrency(kasaPeriodTotals.giris)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Çıkış</p>
                              <p className="text-lg font-bold text-red-600">{formatCurrency(kasaPeriodTotals.cikis)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-gray-500">Bakiye</p>
                              <p className={`text-lg font-bold ${kasaPeriodTotals.bakiye >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(kasaPeriodTotals.bakiye)}
                              </p>
                            </div>
                          </div>
                          
                          {loadingKasaTransactions ? (
                            <div className="p-8 text-center text-gray-500">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                              İşlemler yükleniyor...
                            </div>
                          ) : filteredKasaTransactions.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <Wallet className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                              Bu dönemde işlem bulunamadı
                            </div>
                          ) : (
                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                              <Table>
                                <TableHeader className="sticky top-0 bg-white">
                                  <TableRow className="bg-gray-50">
                                    <TableHead className="w-12">Tip</TableHead>
                                    <TableHead>Seri No</TableHead>
                                    <TableHead>Tarih</TableHead>
                                    <TableHead>Etiket</TableHead>
                                    <TableHead>Açıklama</TableHead>
                                    <TableHead>Cari Hesap</TableHead>
                                    <TableHead className="text-right">Tutar</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filteredKasaTransactions.map((tx) => (
                                    <TableRow key={tx.id} className="hover:bg-gray-50">
                                      <TableCell>
                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${tx.type === 'giris' ? 'bg-green-100' : 'bg-red-100'}`}>
                                          {tx.type === 'giris' ? (
                                            <ChevronDown className="h-4 w-4 text-green-600" />
                                          ) : (
                                            <ChevronUp className="h-4 w-4 text-red-600" />
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="outline" className="font-mono text-xs bg-gray-50">
                                          {tx.seri_no || '-'}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="font-medium whitespace-nowrap">
                                        {formatDate(tx.tarih)}
                                      </TableCell>
                                      <TableCell>
                                        {tx.etiket_adi ? (
                                          <Badge variant="outline" className="text-xs">
                                            {tx.etiket_adi}
                                          </Badge>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="max-w-[200px]">
                                        {tx.aciklama || '-'}
                                      </TableCell>
                                      <TableCell>
                                        {tx.cari_hesap_adi || '-'}
                                      </TableCell>
                                      <TableCell className={`text-right font-semibold whitespace-nowrap ${tx.type === 'giris' ? 'text-green-600' : 'text-red-600'}`}>
                                        {tx.type === 'giris' ? '+' : '-'} {formatCurrency(tx.miktar, tx.para_birimi)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                          
                          {/* Alt Bilgi */}
                          <div className="bg-gray-50 px-6 py-3 border-t flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              Bu dönemde toplam {filteredKasaTransactions.length} işlem
                            </span>
                            <div className="flex gap-4 text-sm">
                              <span className="text-green-600 font-medium">Giriş: {formatCurrency(kasaPeriodTotals.giris)}</span>
                              <span className="text-red-600 font-medium">Çıkış: {formatCurrency(kasaPeriodTotals.cikis)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Kredi Kartı Ekleme/Düzenleme Modal */}
      <Dialog open={krediKartiModal} onOpenChange={setKrediKartiModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-600" />
              {editingKrediKarti ? 'Kredi Kartı Düzenle' : 'Kredi Kartı Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="kart_basligi">Kredi Kartı Başlığı *</Label>
              <Input
                id="kart_basligi"
                placeholder="Örn: Şirket Kurumsal Kart"
                value={krediKartiForm.kart_basligi}
                onChange={(e) => setKrediKartiForm({...krediKartiForm, kart_basligi: e.target.value})}
                data-testid="kredi-karti-baslik-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fortis_subesi">Kartı Kullanan Fortis Şubesi</Label>
              <Select 
                value={krediKartiForm.fortis_subesi} 
                onValueChange={(value) => setKrediKartiForm({...krediKartiForm, fortis_subesi: value})}
              >
                <SelectTrigger data-testid="kredi-karti-sube-select">
                  <SelectValue placeholder="Şube seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="banka_ismi">Banka İsmi *</Label>
              <Input
                id="banka_ismi"
                placeholder="Örn: Garanti BBVA"
                value={krediKartiForm.banka_ismi}
                onChange={(e) => setKrediKartiForm({...krediKartiForm, banka_ismi: e.target.value})}
                data-testid="kredi-karti-banka-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hesap_kesim_tarihi" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  Hesap Kesim Günü
                </Label>
                <Select 
                  value={krediKartiForm.hesap_kesim_tarihi} 
                  onValueChange={(value) => setKrediKartiForm({...krediKartiForm, hesap_kesim_tarihi: value})}
                >
                  <SelectTrigger data-testid="kredi-karti-kesim-select">
                    <SelectValue placeholder="Gün seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {dayOptions.map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="son_odeme_tarihi" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-red-600" />
                  Son Ödeme Günü
                </Label>
                <Select 
                  value={krediKartiForm.son_odeme_tarihi} 
                  onValueChange={(value) => setKrediKartiForm({...krediKartiForm, son_odeme_tarihi: value})}
                >
                  <SelectTrigger data-testid="kredi-karti-odeme-select">
                    <SelectValue placeholder="Gün seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {dayOptions.map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="kart_no">Kart Numarası</Label>
              <Input
                id="kart_no"
                placeholder="**** **** **** ****"
                value={krediKartiForm.kart_no}
                onChange={(e) => setKrediKartiForm({...krediKartiForm, kart_no: e.target.value})}
                data-testid="kredi-karti-no-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKrediKartiModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleKrediKartiSubmit} 
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="kredi-karti-submit-btn"
            >
              {editingKrediKarti ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banka Hesabı Ekleme/Düzenleme Modal */}
      <Dialog open={bankaHesabiModal} onOpenChange={setBankaHesabiModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              {editingBankaHesabi ? 'Banka Hesabı Düzenle' : 'Banka Hesabı Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="banka_adi">Banka Adı *</Label>
              <Input
                id="banka_adi"
                placeholder="Örn: Yapı Kredi"
                value={bankaHesabiForm.banka_adi}
                onChange={(e) => setBankaHesabiForm({...bankaHesabiForm, banka_adi: e.target.value})}
                data-testid="banka-hesabi-banka-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="banka_subesi">Hesabın Açık Olduğu Banka Şubesi</Label>
              <Input
                id="banka_subesi"
                placeholder="Örn: Kadıköy Şubesi"
                value={bankaHesabiForm.banka_subesi}
                onChange={(e) => setBankaHesabiForm({...bankaHesabiForm, banka_subesi: e.target.value})}
                data-testid="banka-hesabi-sube-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fortis_subesi_banka">Hesabı Kullanan Fortis Şubesi</Label>
              <Select 
                value={bankaHesabiForm.fortis_subesi} 
                onValueChange={(value) => setBankaHesabiForm({...bankaHesabiForm, fortis_subesi: value})}
              >
                <SelectTrigger data-testid="banka-hesabi-fortis-sube-select">
                  <SelectValue placeholder="Şube seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hesap_no">Hesap Numarası</Label>
              <Input
                id="hesap_no"
                placeholder="Hesap numarasını girin"
                value={bankaHesabiForm.hesap_no}
                onChange={(e) => setBankaHesabiForm({...bankaHesabiForm, hesap_no: e.target.value})}
                data-testid="banka-hesabi-no-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN *</Label>
              <Input
                id="iban"
                placeholder="TR00 0000 0000 0000 0000 0000 00"
                value={bankaHesabiForm.iban}
                onChange={(e) => setBankaHesabiForm({...bankaHesabiForm, iban: e.target.value})}
                data-testid="banka-hesabi-iban-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankaHesabiModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleBankaHesabiSubmit} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="banka-hesabi-submit-btn"
            >
              {editingBankaHesabi ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nakit Kasa Ekleme/Düzenleme Modal */}
      <Dialog open={nakitKasaModal} onOpenChange={setNakitKasaModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-600" />
              {editingNakitKasa ? 'Nakit Kasa Düzenle' : 'Nakit Kasa Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="kasa_adi">Kasa Adı *</Label>
              <Input
                id="kasa_adi"
                placeholder="Örn: Merkez Kasa"
                value={nakitKasaForm.kasa_adi}
                onChange={(e) => setNakitKasaForm({...nakitKasaForm, kasa_adi: e.target.value})}
                data-testid="nakit-kasa-adi-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sorumlu_kisi">Sorumlu Kişi *</Label>
              <Input
                id="sorumlu_kisi"
                placeholder="Örn: Ahmet Yılmaz"
                value={nakitKasaForm.sorumlu_kisi}
                onChange={(e) => setNakitKasaForm({...nakitKasaForm, sorumlu_kisi: e.target.value})}
                data-testid="nakit-kasa-sorumlu-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fortis_subesi_kasa">Kasanın Bulunduğu Fortis Şubesi</Label>
              <Select 
                value={nakitKasaForm.fortis_subesi} 
                onValueChange={(value) => setNakitKasaForm({...nakitKasaForm, fortis_subesi: value})}
              >
                <SelectTrigger data-testid="nakit-kasa-sube-select">
                  <SelectValue placeholder="Şube seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNakitKasaModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleNakitKasaSubmit} 
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="nakit-kasa-submit-btn"
            >
              {editingNakitKasa ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingHesapEkle;
